import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { lazy, Suspense, useEffect, useState } from "react";



import { useQuery } from "@tanstack/react-query";

import { useServerFn } from "@tanstack/react-start";
import { HERO_CLASSIC_COVERS } from "@/lib/hero-covers";
import { getCollectorSocialProof } from "@/lib/xp.functions";
import { CollectorSocialProof } from "@/components/CollectorSocialProof";
import { Navbar1 } from "@/components/ui/navbar-1";


import { useIsMobile } from "@/hooks/use-mobile";
import { FooterReveal } from "@/components/ui/footer-reveal";
import { ImageStreamHero } from "@/components/ui/image-stream-hero";
import { DiscCascadeShowcase } from "@/components/landing/DiscCascadeShowcase";

const SiteFooter = lazy(() =>

  import("@/components/SiteFooter").then((m) => ({ default: m.SiteFooter })),
);


import { LazyVisible } from "@/components/LazyVisible";


const LandingValueSections = lazy(() =>
  import("@/components/landing/ValueStorySections").then((m) => ({
    default: m.LandingValueSections,
  })),
);

// Spotify covers default to 640px. Swap to 300px variant for non-hero mockups.
const smallCover = (src: string): string =>
  !src ? src : src.replace("ab67616d0000b273", "ab67616d00001e02");

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Viny Club | Comunidade dos colecionadores de vinil" },
      {
        name: "description",
        content:
          "Organize seus discos, descubra discografias em vinil, ouça prévias e compartilhe sua coleção com outros colecionadores no Viny Club.",
      },
      {
        name: "keywords",
        content:
          "comunidade de colecionadores de vinil, colecionadores de vinil Brasil, discos de vinil, catálogo de discos de vinil, link na bio vinil, prensagens raras, mini site coleção vinil",
      },
      { property: "og:title", content: "Viny Club | Comunidade dos colecionadores de vinil" },
      {
        property: "og:description",
        content:
          "Organize sua coleção, descubra discografias em vinil e encontre outros colecionadores.",
      },
      { property: "og:url", content: "https://viny.club" },
      { property: "og:type", content: "website" },
      { property: "og:image", content: "https://viny.club/og-image.jpg" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Viny Club | Comunidade dos colecionadores de vinil" },
      {
        name: "twitter:description",
        content:
          "Organize sua coleção, descubra discografias em vinil e encontre outros colecionadores.",
      },
      { name: "twitter:image", content: "https://viny.club/og-image.jpg" },
    ],
    links: [
      { rel: "canonical", href: "https://viny.club" },
    ],
  }),
  component: Landing,
});

function Landing() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  // Mobile logado: pula a LP e vai direto para a plataforma.
  // Perf: o SDK de auth entra por import dinâmico, fora do bundle inicial da LP.
  useEffect(() => {
    if (!isMobile) return;
    let cancelled = false;
    const id = window.setTimeout(async () => {
      try {
        const { supabase } = await import("@/integrations/supabase/client");
        const { data } = await supabase.auth.getSession();
        if (!cancelled && data.session) navigate({ to: "/explore", replace: true });
      } catch {}
    }, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(id);
    };
  }, [isMobile, navigate]);

  // Capture referral code from ?ref= and persist for signup flow
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get("ref");
      if (ref) {
        const clean = ref.toLowerCase().replace(/[^a-z0-9_]/g, "").slice(0, 40);
        if (clean) localStorage.setItem("viny_ref", clean);
      }
    } catch {}
  }, []);




  return (
    <div className="min-h-screen bg-white text-foreground">
      {/* NAV — escuro sobre hero clara */}
      <Navbar1
        darkAtTop
        shrinkOnScroll
        hideOnFooter
        items={[
          { label: "Recursos", href: "#recursos" },
          { label: "Comunidade", href: "#community" },
        ]}
      />


      {/* CONTEÚDO — desliza por cima do footer, com a base arredondada */}
      <main className="relative z-10 rounded-b-[50px] bg-background">

      <ImageStreamHero socialProof={<CollectorSocialProofWrapper />} />

       <DiscCascadeShowcase />


      <LazyVisible minHeight={1400} rootMargin="240px 0px">
        <Suspense fallback={<div className="min-h-[1400px] bg-background" />}>
          <LandingValueSections
            covers={HERO_CLASSIC_COVERS.map((cover) => smallCover(cover.src))}
          />
        </Suspense>
      </LazyVisible>


      </main>

      <LazyVisible minHeight={300} rootMargin="300px 0px">
        <FooterReveal>
          <Suspense fallback={<div className="min-h-[300px]" />}>
            <SiteFooter />
          </Suspense>
        </FooterReveal>
      </LazyVisible>




    </div>
  );
}






/**
 * A prova social monta no CTA final e reaproveita o cache por 5 minutos.
 */
function CollectorSocialProofWrapper() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted ? <CollectorSocialProofData /> : null;
}

function CollectorSocialProofData() {
  const fn = useServerFn(getCollectorSocialProof);
  const { data } = useQuery({
    queryKey: ["collector-social-proof"],
    queryFn: () => fn({ data: undefined }),
    staleTime: 5 * 60_000,
  });
  if (!data) return null;
  return (
    <div className="flex justify-center">
      <CollectorSocialProof count={data.count} people={data.people} />
    </div>
  );
}

